import * as math from 'mathjs'; // For ln, abs, sqrt etc.

// CLINICAL IMPROVEMENT: Increased from 4.0 to 6.0 for more realistic health score transitions.
// At 4.0 SD, health scores dropped to zero too quickly. 6.0 SD provides better granularity.
export const MAX_Z_FOR_ZERO_HEALTH = 6.0;

// Helper function to calculate BSA using Mosteller formula
const calculateBSA = (heightCm, weightKg) => {
  if (heightCm > 0 && weightKg > 0) {
    return math.sqrt((heightCm * weightKg) / 3600);
  }
  return null;
};

// Helper function to calculate dynamic target (ported from V1)
export const calculateDynamicTarget = (coefficientsKey, patientDemographics, targetFormulasConfig) => {
  const MISSING_DEMOGRAPHICS_INDICATOR = { error: "Missing Demographics", required: [] };
  let calculationDetails = [];
  let finalFormulaParts = [];

  if (!targetFormulasConfig || !Array.isArray(targetFormulasConfig)) {
    return { error: "Missing or invalid Target Formulas Configuration (expected array)" };
  }

  if (!patientDemographics) {
    MISSING_DEMOGRAPHICS_INDICATOR.required = ['age', 'sex']; // Basic requirements at minimum
    return MISSING_DEMOGRAPHICS_INDICATOR;
  }

  const formulaData = targetFormulasConfig.find(p => p.key === coefficientsKey);
  const formulaCoeff = formulaData; // For compatibility with the rest of the function

  if (!formulaCoeff || !formulaCoeff.coefficients) {
    return { error: `Formula definition not found for key ${coefficientsKey}` };
  }

  const { coefficients } = formulaCoeff;
  const { age, sex, ethnicity, isPregnant, isPostMenopausal, height_cm, weight_kg, athleticismLevel } = patientDemographics;

  const ethnicityToRaceKeyMap = {
    'White': 'race_white',
    'Black or African American': 'race_black',
    'Asian': 'race_asian',
    'Hispanic or Latino': 'race_hispanic',
    'American Indian or Alaska Native': 'race_native',
    'Native Hawaiian or Other Pacific Islander': 'race_pacific',
  };
  const patientRaceKey = ethnicity ? ethnicityToRaceKeyMap[ethnicity] : null;

  // --- Start: Comprehensive Missing Fields Check ---
  let missingFields = [];
  // Calculate BSA once if its coefficient exists and is non-zero, to use in missingFields check
  const bsaCoeffExistsAndNonZero = coefficients.hasOwnProperty('bsa') && parseFloat(coefficients.bsa) !== 0;
  const tempBsa = (bsaCoeffExistsAndNonZero && height_cm !== undefined && weight_kg !== undefined) ? calculateBSA(height_cm, weight_kg) : null;

  const factorChecks = [
    { key: 'age', coeff: coefficients.age, data: age, name: 'age' },
    { key: 'female', coeff: coefficients.female, data: sex, name: 'sex' },
    { key: 'pregnant', coeff: coefficients.pregnant, data: isPregnant, name: 'isPregnant' },
    { key: 'post_menopause', coeff: coefficients.post_menopause, data: isPostMenopausal, name: 'isPostMenopausal' },
    { key: 'height', coeff: coefficients.height, data: height_cm, name: 'height_cm' },
    { 
      key: 'bsa', 
      coeff: coefficients.bsa, 
      data: tempBsa, // Use pre-calculated tempBsa for check
      name: 'height_cm or weight_kg for BSA calculation',
      isMissingOverride: () => bsaCoeffExistsAndNonZero && (height_cm === undefined || weight_kg === undefined)
    }
  ];

  factorChecks.forEach(fc => {
    const coeffVal = parseFloat(fc.coeff);
    if (!isNaN(coeffVal) && coeffVal !== 0) { // Only require data if coefficient is defined and non-zero
      if (fc.isMissingOverride && fc.isMissingOverride()) {
        if (!missingFields.includes(fc.name)) missingFields.push(fc.name);
      } else if (fc.data === undefined && !fc.isMissingOverride) {
         if (!missingFields.includes(fc.name)) missingFields.push(fc.name);
      }
    }
  });
  
  const hasAnyAthleticismCoeffNonZero = Object.keys(coefficients).some(key => key.startsWith('athleticism_') && parseFloat(coefficients[key]) !== 0);
  if (hasAnyAthleticismCoeffNonZero && !athleticismLevel) {
    if (!missingFields.includes('athleticismLevel')) missingFields.push('athleticismLevel');
  }

  if (missingFields.length > 0) {
    MISSING_DEMOGRAPHICS_INDICATOR.required = missingFields;
    return MISSING_DEMOGRAPHICS_INDICATOR;
  }
  // --- End: Comprehensive Missing Fields Check ---

  // Recalculate BSA for actual use if its coefficient exists (even if zero, for display)
  const bsa = (coefficients.hasOwnProperty('bsa') && height_cm !== undefined && weight_kg !== undefined) ? calculateBSA(height_cm, weight_kg) : null;
  if (coefficients.hasOwnProperty('bsa') && bsaCoeffExistsAndNonZero && bsa === null && height_cm !== undefined && weight_kg !== undefined) {
      // This case implies height_cm or weight_kg were invalid for BSA calculation (e.g. zero)
      return { error: "BSA calculation failed due to invalid height/weight values.", required:['valid height_cm', 'valid weight_kg']};
  }

  let target = 0;
  const interceptValue = coefficients.intercept !== undefined ? parseFloat(coefficients.intercept) : 0;
  if (isNaN(interceptValue)) return { error: `Invalid intercept value for ${coefficientsKey}`};
  target += interceptValue;
  calculationDetails.push({ term: "Intercept", value: interceptValue, calculation: `${interceptValue.toFixed(2)}` });
  finalFormulaParts.push(interceptValue.toFixed(2));

  const orderedFactorDefinitions = [
    {
      coeffKey: 'age', labelName: 'Age', patientData: age,
      getPivot: () => formulaCoeff.pivot_age !== undefined ? parseFloat(formulaCoeff.pivot_age) : 40,
      calcPatientPart: (data, pivot) => (data !== undefined ? data : pivot) - pivot, // If age N/A, (pivot-pivot)=0
      formatPatientPart: (data, pivot) => `(${data === undefined ? 'N/A' : data} - ${pivot})`
    },
    {
      coeffKey: 'female', labelName: 'Female Sex', patientData: sex, // Label changed for clarity
      getPivot: () => null,
      calcPatientPart: (data) => data && typeof data === 'string' && data.toLowerCase() === 'female' ? 1 : 0,
      formatPatientPart: (patientPartVal, pivot, data) => `${patientPartVal} (Patient: ${data || 'Sex N/A'})` // User requested format
    },
    {
      coeffKey: 'pregnant', labelName: 'Pregnancy', patientData: isPregnant,
      getPivot: () => null,
      calcPatientPart: (data) => data === true ? 1 : 0,
      formatPatientPart: (patientPartVal, pivot, data) => `${patientPartVal} (Patient: ${data === undefined ? 'N/A' : data})`
    },
    {
      coeffKey: 'post_menopause', labelName: 'Post-Menopause', patientData: isPostMenopausal,
      getPivot: () => null,
      calcPatientPart: (data) => data === true ? 1 : 0,
      formatPatientPart: (patientPartVal, pivot, data) => `${patientPartVal} (Patient: ${data === undefined ? 'N/A' : data})`
    },
    {
      coeffKey: 'height', labelName: 'Height', patientData: height_cm, // refers to height_cm for patient data
      getPivot: () => formulaCoeff.pivot_height !== undefined ? parseFloat(formulaCoeff.pivot_height) : 165,
      calcPatientPart: (data, pivot) => data !== undefined ? ((data - pivot) / 10) : 0, // If height N/A, 0 contribution
      formatPatientPart: (data, pivot) => `((${data === undefined ? 'N/A' : data} - ${pivot}) / 10)`
    },
    {
      coeffKey: 'bsa', labelName: 'BSA', patientData: bsa, // bsa is pre-calculated
      getPivot: () => formulaCoeff.pivot_bsa !== undefined ? parseFloat(formulaCoeff.pivot_bsa) : 1.7,
      calcPatientPart: (data, pivot) => data !== null ? (data - pivot) : 0, // If bsa N/A, 0 contribution
      formatPatientPart: (data, pivot) => `(${data === null || data === undefined ? 'N/A' : data.toFixed(2)} - ${pivot.toFixed(2)})`
    },
    // Race/Ethnicity Factors
    {
      coeffKey: 'race_white', labelName: 'Race (White)', patientData: patientRaceKey,
      calcPatientPart: (data) => data === 'race_white' ? 1 : 0,
      formatPatientPart: (patientPartVal) => `${patientPartVal}`
    },
    {
      coeffKey: 'race_black', labelName: 'Race (Black)', patientData: patientRaceKey,
      calcPatientPart: (data) => data === 'race_black' ? 1 : 0,
      formatPatientPart: (patientPartVal) => `${patientPartVal}`
    },
    {
      coeffKey: 'race_asian', labelName: 'Race (Asian)', patientData: patientRaceKey,
      calcPatientPart: (data) => data === 'race_asian' ? 1 : 0,
      formatPatientPart: (patientPartVal) => `${patientPartVal}`
    },
    {
      coeffKey: 'race_hispanic', labelName: 'Race (Hispanic)', patientData: patientRaceKey,
      calcPatientPart: (data) => data === 'race_hispanic' ? 1 : 0,
      formatPatientPart: (patientPartVal) => `${patientPartVal}`
    },
    {
      coeffKey: 'race_native', labelName: 'Race (Native)', patientData: patientRaceKey,
      calcPatientPart: (data) => data === 'race_native' ? 1 : 0,
      formatPatientPart: (patientPartVal) => `${patientPartVal}`
    },
    {
      coeffKey: 'race_pacific', labelName: 'Race (Pacific Islander)', patientData: patientRaceKey,
      calcPatientPart: (data) => data === 'race_pacific' ? 1 : 0,
      formatPatientPart: (patientPartVal) => `${patientPartVal}`
    }
  ];

  orderedFactorDefinitions.forEach(def => {
    const termName = `${def.labelName} Adjustment`;
    // Use a more robust check for race factors
    const isRaceFactor = def.coeffKey.startsWith('race_');
    
    // Only process the race factor that matches the patient's ethnicity
    if (isRaceFactor && def.coeffKey !== patientRaceKey) {
      return; // Skip non-matching race factors
    }

    if (coefficients.hasOwnProperty(def.coeffKey)) {
      const coeffValue = parseFloat(coefficients[def.coeffKey]);
      if (isNaN(coeffValue)) {
        calculationDetails.push({ term: termName, value: NaN, calculation: `Invalid coefficient for ${def.coeffKey}` });
        finalFormulaParts.push("NaN");
        if (!isNaN(target)) target = NaN;
        return;
      }

      const pivot = def.getPivot ? def.getPivot() : null;
      if (def.getPivot && isNaN(pivot)) {
        calculationDetails.push({ term: termName, value: NaN, calculation: `Invalid pivot value for ${def.labelName}` });
        finalFormulaParts.push("NaN");
        if (!isNaN(target)) target = NaN;
        return;
      }
      
      const patientPartValue = def.calcPatientPart(def.patientData, pivot);
      const patientPartString = def.formatPatientPart(patientPartValue, pivot, def.patientData);
      
      const adjustment = coeffValue * patientPartValue;
      if (isNaN(adjustment)) {
        if(!isNaN(target)) target = NaN;
      } else if (!isNaN(target)) {
        target += adjustment;
      }

      // For race, show the patient's ethnicity in the term name for clarity
      const displayTermName = isRaceFactor ? `Race Adjustment (${ethnicity || 'N/A'})` : termName;

      calculationDetails.push({
        term: displayTermName,
        value: isNaN(adjustment) ? NaN : adjustment,
        calculation: `${coeffValue.toFixed(3)} * ${patientPartString} = ${isNaN(adjustment) ? 'NaN' : adjustment.toFixed(2)}`
      });
      finalFormulaParts.push(isNaN(adjustment) ? "NaN" : adjustment.toFixed(2));
    } else {
      // This block now correctly handles factors that are universally checked but not applicable to a specific parameter
      calculationDetails.push({ term: termName, value: 0, calculation: "Not applicable for this parameter" });
      finalFormulaParts.push("0.00");
    }
  });
  
  if (isNaN(target)) return { error: `Target calculation resulted in NaN for ${coefficientsKey} during factor processing.` };

  // Athleticism (Refactored for universal display)
  const athleticismTermName = `Athleticism Adjustment (${athleticismLevel || 'N/A'})`;
  const athleticismCoeffKey = athleticismLevel ? `athleticism_${athleticismLevel.toLowerCase()}` : 'athleticism_non_athletic';
  let athleticismAdjustment = 0;
  let athleticismCalculationString = "Not applicable for this parameter";

  if (Object.keys(coefficients).some(key => key.startsWith('athleticism_'))) {
    if (athleticismCoeffKey && coefficients.hasOwnProperty(athleticismCoeffKey)) {
      const coeffValue = parseFloat(coefficients[athleticismCoeffKey]);
      if (!isNaN(coeffValue)) {
        athleticismAdjustment = coeffValue;
        athleticismCalculationString = `${athleticismAdjustment.toFixed(2)}`;
      } else {
        athleticismAdjustment = NaN;
        athleticismCalculationString = `Invalid coefficient for ${athleticismCoeffKey}`;
      }
    } else {
      athleticismCalculationString = `0.00 (No specific coefficient for this level, or level undefined)`;
    }
  }
  
  if (isNaN(athleticismAdjustment)) {
    if (!isNaN(target)) target = NaN;
  } else if (!isNaN(target)) {
    target += athleticismAdjustment;
  }

  calculationDetails.push({
    term: athleticismTermName,
    value: athleticismAdjustment,
    calculation: athleticismCalculationString
  });
  finalFormulaParts.push(isNaN(athleticismAdjustment) ? "NaN" : athleticismAdjustment.toFixed(2));
  
  if (isNaN(target)) return { error: `Target calculation resulted in NaN for ${coefficientsKey}` };
  
  const finalFormulaString = finalFormulaParts.map(partStr => {
      const num = parseFloat(partStr); // Ensure partStr is treated as a string for parseFloat
      if (isNaN(num)) return "NaN";
      // Ensure "0.00" for zero, and signed for others if they were stored as signed strings
      if (num === 0) return "0.00";
      return num.toFixed(2); 
  }).join(" + ").replace(/\+ -/g, "- ") + ` = ${target.toFixed(2)}`; // Make "X + -Y" look like "X - Y"
  
  return {
    value: target,
    calculationDetails,
    finalFormula: finalFormulaString
  };
};

// Z-Score calculation function for V2, handles asymmetric SDs
export const calculateZScore_V2 = (currentValue, idealTarget, paramConfig) => {
  if (currentValue === null || currentValue === undefined || idealTarget === null || idealTarget === undefined) {
    return { error: "Missing current value or ideal target for Z-score calculation." };
  }
  if (isNaN(currentValue) || isNaN(idealTarget)) {
    return { error: "Current value or ideal target is not a number."}
  }

  if (!paramConfig) {
    return { error: "Missing parameter configuration for Z-score calculation." };
  }

  let selectedSigma;
  // Prioritize explicit stdDeviation_low/high if present in paramConfig
  let sigmaLow = paramConfig.stdDeviation_low !== undefined ? parseFloat(paramConfig.stdDeviation_low) : undefined;
  let sigmaHigh = paramConfig.stdDeviation_high !== undefined ? parseFloat(paramConfig.stdDeviation_high) : undefined;
  
  // Fallback to single stdDeviation or V1's sd.value structure
  let sigmaSingle = paramConfig.stdDeviation !== undefined ? parseFloat(paramConfig.stdDeviation) : undefined;
  if (sigmaSingle === undefined && paramConfig.sd && paramConfig.sd.value !== undefined) {
      sigmaSingle = parseFloat(paramConfig.sd.value);
  }


  if (sigmaLow !== undefined && !isNaN(sigmaLow) && sigmaHigh !== undefined && !isNaN(sigmaHigh)) {
    if (currentValue < idealTarget) {
      selectedSigma = sigmaLow;
    } else if (currentValue > idealTarget) {
      selectedSigma = sigmaHigh;
    } else { // currentValue === idealTarget
      selectedSigma = sigmaHigh; // or sigmaLow, doesn't matter as Z will be 0, but pick one consistently
    }
  } else if (sigmaSingle !== undefined && !isNaN(sigmaSingle)) {
    selectedSigma = sigmaSingle;
  } else {
    return { error: "Missing or invalid standard deviation in parameter configuration." };
  }

  if (selectedSigma === 0) {
    if (currentValue === idealTarget) return { zScore: 0, selectedSigma: 0 };
    return { error: "Standard deviation is zero, cannot calculate Z-score for non-target value." };
  }
  if (isNaN(selectedSigma)) {
    return { error: "Selected standard deviation is not a number."}
  }
  
  const zScore = (currentValue - idealTarget) / selectedSigma;
  if (isNaN(zScore)) return { error: "Z-score calculation resulted in NaN."}

  return { zScore: zScore, selectedSigma: selectedSigma };
};

// Function to calculate a true WEIGHTED Average Absolute Z-Score
export const calculateWeightedAverageAbsoluteZScore = (parameterDetailsArray) => {
  if (!parameterDetailsArray || !Array.isArray(parameterDetailsArray)) {
    return { averageAbsoluteZScore: null, count: 0, sumOfWeightedZScores: 0, sumOfWeights: 0, error: "Invalid input: Expected an array." };
  }

  let sumOfWeightedZScores = 0;
  let sumOfWeights = 0;
  let count = 0;

  for (const param of parameterDetailsArray) {
    // A parameter is only included if it has a valid Z-score and a valid, non-zero weight.
    if (param && param.isScored !== false && param.zScore !== null && typeof param.zScore === 'number' && !param.error && !isNaN(param.zScore) &&
        param.overall_significance_weight !== null && typeof param.overall_significance_weight === 'number' && !isNaN(param.overall_significance_weight) && param.overall_significance_weight > 0) {
      
      const weight = param.overall_significance_weight / 100.0; // Normalize weight to be a decimal percentage
      const absZ = math.abs(param.zScore);

      sumOfWeightedZScores += absZ * weight;
      sumOfWeights += weight;
      count++;
    }
  }

  if (sumOfWeights > 0) {
    const average = sumOfWeightedZScores / sumOfWeights;
    return { averageAbsoluteZScore: average, count, sumOfWeightedZScores, sumOfWeights };
  } else {
    return { averageAbsoluteZScore: 0, count: 0, sumOfWeightedZScores: 0, sumOfWeights: 0 };
  }
};


// Function to calculate a simple, unweighted average of absolute Z-scores.
// This is used for specific calculations like the PhenoAge panel adjustment
// where weighting is not desired.
export const calculateAverageAbsoluteZScore = (parameterDetailsArray) => {
  if (!parameterDetailsArray || !Array.isArray(parameterDetailsArray)) {
    return { averageAbsoluteZScore: null, count: 0, sumOfAbsoluteZScores: 0, error: "Invalid input: Expected an array." };
  }

  let sumOfAbsoluteZScores = 0;
  let count = 0;

  for (const param of parameterDetailsArray) {
    // A parameter is only included if it has a valid Z-score.
    if (param && param.isScored !== false && param.zScore !== null && typeof param.zScore === 'number' && !param.error && !isNaN(param.zScore)) {
      sumOfAbsoluteZScores += math.abs(param.zScore);
      count++;
    }
  }

  if (count > 0) {
    const average = sumOfAbsoluteZScores / count;
    return { averageAbsoluteZScore: average, count, sumOfAbsoluteZScores };
  } else {
    return { averageAbsoluteZScore: 0, count: 0, sumOfAbsoluteZScores: 0 };
  }
};


// Placeholder for the main V2 scoring function we will build incrementally
export const calculateHealthScoresV2 = (patientData, targetFormulasConfig, scoringConfig) => {
  // This function will eventually orchestrate all calculations for HealthSnapshotV2
  // For now, it's a placeholder. The HealthSnapshotV2 component will call individual
  // calculation functions directly for the step-by-step display.
  
  const results = {
    parameterDetailsTable: [], // This will be populated by HealthSnapshotV2 component for display
    totalAverageAbsoluteZScore: null,
    totalParameterCountForZScore: 0,
    systemScores: { /* To be defined */ },
    lifestyleScores: { /* To be defined */ },
    errors: []
  };

  if (!patientData || !patientData.demographics || !patientData.parameters) {
    results.errors.push("Patient data is incomplete for calculateHealthScoresV2.");
    // Early exit or handle as needed
  }
  if (!targetFormulasConfig) results.errors.push("Target formulas configuration missing.");
  if (!scoringConfig || !scoringConfig.parameters) results.errors.push("Scoring configuration missing or invalid.");


  // The detailed, step-by-step processing and table data generation
  // will primarily happen in the HealthSnapshotV2.js component for now,
  // to facilitate the "show your work" display.
  // This engine function might later be expanded to do more centralized processing
  // if HealthSnapshotV2 needs a more consolidated data object.

  console.log("calculateHealthScoresV2 (V2 Engine) called - currently a placeholder.");

  return results;
};

// Function to calculate System and Lifestyle Domain Average Absolute Z-Score Deviations
export const calculateSystemAndLifestyleDeviations = (parameterDetailsArray, mergedParameterWeights, nutritionParameterKeys) => {
  const systemDeviations = {};
  const lifestyleDomainDeviations = {};

  if (!parameterDetailsArray || !Array.isArray(parameterDetailsArray) || !mergedParameterWeights || !mergedParameterWeights.parameters) {
    return { systemDeviations, lifestyleDomainDeviations, error: "Invalid input or missing parameter weights." };
  }

  // Initialize accumulators
  const knownSystems = ["neurological", "cardiovascular", "respiratory", "metabolic", "filtration", "musculoskeletal", "immune"];
  const knownDimensions = ["S", "F", "R"];
  const dimensionMapping = { "S": "structure", "F": "function", "R": "risk" };
  knownSystems.forEach(sysKey => {
    systemDeviations[sysKey] = {};
    knownDimensions.forEach(dimKey => {
      const displayKey = dimensionMapping[dimKey];
      systemDeviations[sysKey][displayKey] = { sumWeightedAbsZ: 0, sumOfWeights: 0, count: 0, averageWeightedAbsZ: 0, healthScore: 0, contributingParams: [] };
    });
  });

  const knownLifestyleDomains = ["nutrition", "movement", "recovery", "mental_health"];
  knownLifestyleDomains.forEach(domainKey => {
    lifestyleDomainDeviations[domainKey] = { sumWeightedAbsZ: 0, sumOfWeights: 0, count: 0, averageWeightedAbsZ: 0, healthScore: 0, contributingParams: [], coreComponents: [] };
  });

  for (const paramDetail of parameterDetailsArray) {
    if (paramDetail && paramDetail.isScored !== false && paramDetail.zScore !== null && typeof paramDetail.zScore === 'number' && !paramDetail.error && !isNaN(paramDetail.zScore)) {
      const paramKey = paramDetail.key;
      const absZScore = math.abs(paramDetail.zScore);
      const weightConfig = mergedParameterWeights.parameters[paramKey];

      if (weightConfig) {
        // Process System Weights
        if (weightConfig.systems) {
            const systemsArray = Array.isArray(weightConfig.systems) ? weightConfig.systems : [weightConfig.systems];

            for (const systemEntry of systemsArray) {
              for (const systemKey in systemEntry) {
                const lowerCaseSystemKey = systemKey.toLowerCase();
                if (systemDeviations[lowerCaseSystemKey]) {
                  const systemData = systemEntry[systemKey];
                  
                  // --- START: New Unified Priority Score Logic ---
                  const sWeight = systemData.S?.final_weight || (typeof systemData.S === 'number' ? systemData.S : 0);
                  const fWeight = systemData.F?.final_weight || (typeof systemData.F === 'number' ? systemData.F : 0);
                  const rWeight = systemData.R?.final_weight || (typeof systemData.R === 'number' ? systemData.R : 0);
                  
                  const averageSystemWeight = (sWeight + fWeight + rWeight) / 3;
                  const priorityScore = absZScore * (averageSystemWeight / 100);
                  
                  let prioritySystem = 'Low';
                  if (absZScore >= 1.0) {
                      if (priorityScore >= 1.5) prioritySystem = 'High';
                      else if (priorityScore >= 0.75) prioritySystem = 'Moderate';
                  } else {
                      prioritySystem = 'Optimal';
                  }
                  // --- END: New Unified Priority Score Logic ---

                  // Check if this param has already been processed for this system to avoid redundant calculations
                  const alreadyProcessed = systemDeviations[lowerCaseSystemKey].structure.contributingParams.some(p => p.key === paramKey);

                  if (!alreadyProcessed) {
                      const paramDataForPush = {
                          ...paramDetail,
                          priorityScore: priorityScore,
                          priority: prioritySystem,
                          averageSystemWeight: averageSystemWeight,
                          sWeight,
                          fWeight,
                          rWeight,
                          unit: paramDetail.unit || '',
                          normalRange: paramDetail.normalRange,
                          sd: paramDetail.sd,
                      };
                      // Add the parameter with its unified score to each dimension's list
                      systemDeviations[lowerCaseSystemKey].structure.contributingParams.push(paramDataForPush);
                      systemDeviations[lowerCaseSystemKey].function.contributingParams.push(paramDataForPush);
                      systemDeviations[lowerCaseSystemKey].risk.contributingParams.push(paramDataForPush);
                  }

                  // Now, separately, contribute to the dimension-specific health scores
                  for (const dimKey in systemData) {
                    const systemDimWeightInfo = systemData[dimKey];
                    let finalWeight = 0;
                    if (typeof systemDimWeightInfo === 'number') {
                      finalWeight = systemDimWeightInfo;
                    } else if (systemDimWeightInfo && typeof systemDimWeightInfo.final_weight === 'number') {
                      finalWeight = systemDimWeightInfo.final_weight;
                    }

                    const displayDimKey = dimensionMapping[dimKey] || dimKey;

                    if (systemDeviations[lowerCaseSystemKey][displayDimKey] && finalWeight > 0) {
                      const linearWeight = finalWeight / 100;
                      systemDeviations[lowerCaseSystemKey][displayDimKey].sumWeightedAbsZ += absZScore * linearWeight;
                      systemDeviations[lowerCaseSystemKey][displayDimKey].sumOfWeights += linearWeight;
                      systemDeviations[lowerCaseSystemKey][displayDimKey].count++;
                    }
                  }
                }
              }
            }
        }
        
        // Process Lifestyle Domain Weights (Reverting to linear calculation)
        if (nutritionParameterKeys && nutritionParameterKeys.has(paramKey)) {
            const domainKey = 'nutrition';
            const finalWeight = weightConfig.overall_clinical_significance_weight || 0;
            if (finalWeight > 0) {
                const linearWeight = finalWeight / 100;
                lifestyleDomainDeviations[domainKey].sumWeightedAbsZ += absZScore * linearWeight;
                lifestyleDomainDeviations[domainKey].sumOfWeights += linearWeight;
                lifestyleDomainDeviations[domainKey].count++;
                lifestyleDomainDeviations[domainKey].contributingParams.push({
                    ...paramDetail,
                    specificImpact: absZScore * linearWeight,
                    priority: 'Normal',
                    originalWeightForDomain: finalWeight,
                    is_core_component: true
                });
            }
        } else if (weightConfig.lifestyle_domains && Array.isArray(weightConfig.lifestyle_domains)) {
          weightConfig.lifestyle_domains.forEach(domainInfo => {
            const domainKey = domainInfo.domain;
            if (lifestyleDomainDeviations[domainKey] && typeof domainInfo.weight === 'number' && domainInfo.weight > 0 && domainInfo.is_included) {
              const finalWeight = domainInfo.weight;
              const linearWeight = finalWeight / 100;
              lifestyleDomainDeviations[domainKey].sumWeightedAbsZ += absZScore * linearWeight;
              lifestyleDomainDeviations[domainKey].sumOfWeights += linearWeight;
              lifestyleDomainDeviations[domainKey].count++;

              const specificImpactLifestyle = absZScore * linearWeight;
              let priorityLifestyle = 'Low';
              if (absZScore >= 1.0) {
                if (specificImpactLifestyle >= 1.5) priorityLifestyle = 'High';
                else if (specificImpactLifestyle >= 0.75) priorityLifestyle = 'Moderate';
              } else {
                priorityLifestyle = 'Optimal';
              }
              
              lifestyleDomainDeviations[domainKey].contributingParams.push({ 
                ...paramDetail,
                specificImpact: specificImpactLifestyle,
                priority: priorityLifestyle,
                originalWeightForDomain: finalWeight,
                is_core_component: domainInfo.is_core_component
              });
              if (domainInfo.is_core_component) {
                if (!lifestyleDomainDeviations[domainKey].coreComponents.find(c => c.key === paramKey)) {
                    lifestyleDomainDeviations[domainKey].coreComponents.push({
                        key: paramKey,
                        name: paramDetail.parameterName,
                        zScore: paramDetail.zScore, 
                        isAbnormal: math.abs(paramDetail.zScore) >= 1.0 
                    });
                }
              }
            }
          });
        }
      }
    }
  }

  // Calculate weighted averages and health scores
  for (const systemKey in systemDeviations) {
    for (const dimKey in systemDeviations[systemKey]) {
      const data = systemDeviations[systemKey][dimKey];
      if (data.sumOfWeights > 0) {
        data.averageWeightedAbsZ = data.sumWeightedAbsZ / data.sumOfWeights;
        if (dimKey === 'risk') {
          data.healthScore = Math.min(100, (data.averageWeightedAbsZ / MAX_Z_FOR_ZERO_HEALTH) * 100);
        } else {
          data.healthScore = Math.max(0, 100 - (data.averageWeightedAbsZ / MAX_Z_FOR_ZERO_HEALTH) * 100);
        }
      } else {
        data.averageWeightedAbsZ = 0;
        if (dimKey === 'risk') {
          data.healthScore = 0;
        } else {
          data.healthScore = 100;
        }
      }
      // Sort by the new unified priority score
      data.contributingParams.sort((a, b) => b.priorityScore - a.priorityScore);
    }
  }

  for (const domainKey in lifestyleDomainDeviations) {
    const data = lifestyleDomainDeviations[domainKey];
    if (data.sumOfWeights > 0) {
      data.averageWeightedAbsZ = data.sumWeightedAbsZ / data.sumOfWeights;
      data.healthScore = Math.max(0, 100 - (data.averageWeightedAbsZ / MAX_Z_FOR_ZERO_HEALTH) * 100);
    } else {
      data.averageWeightedAbsZ = 0;
      data.healthScore = null;
    }
    data.contributingParams.sort((a,b) => b.specificImpact - a.specificImpact);
    data.coreComponents.sort((a,b) => math.abs(b.zScore) - math.abs(a.zScore));
  }

  return { systemDeviations, lifestyleDomainDeviations };
};
